/**
 * Stories — ephemeral, audience-scoped 24h media posts.
 *
 * Threat model: the server is honest-but-curious + actively malicious. It can
 * read every byte that crosses the wire, reorder/replay/drop blobs, and try
 * to substitute one story's ciphertext under another story's metadata. The
 * audience for a story is *implicit*: a story is encrypted once with a
 * per-story 32-byte StoryKey, and the StoryKey is handed to each chosen
 * recipient through the existing pairwise E2EE channel
 * (see StoryKeyDistribution.ts). Recipients NOT in the audience never see the
 * StoryKey and therefore cannot decrypt the blob even if they fetch it.
 *
 * Hardening properties — mirroring iOS Story.swift:
 *
 * 1. **Per-story key, not the profile key.** A fresh 32-byte secret per story
 *    bounds the blast radius — compromise of one story cannot lead to recovery
 *    of any other story or the profile blob.
 *
 * 2. **AAD-bound metadata.** AES-GCM AAD carries the canonical (sorted-keys)
 *    JSON bytes of the story's metadata. A hostile server CANNOT take Alice's
 *    encrypted blob for storyId=A and serve it under metadata.storyId=B — the
 *    AES-GCM open fails with `StoryError.openFailure`.
 *
 * 3. **Client-enforced TTL.** `StoryCipher.open` checks `at > createdAt + 24h`
 *    *before* invoking AES-GCM and returns `StoryError.expired`. The server
 *    cannot extend a story's life by replaying an old blob.
 *
 * 4. **Bounded blob size.** `maxStoryBlobBytes` (50 MiB) caps inputs before
 *    any AES-GCM work is done — a DoS floor.
 *
 * Mirrors iOS `kaether_for_ios/Stoat/E2EE/Story.swift`.
 */

// ---------------------------------------------------------------------------
// Error type
// ---------------------------------------------------------------------------

export type StoryError =
  | { kind: "invalidKeyLength"; got: number }
  | { kind: "sealFailure" }
  | { kind: "openFailure" }
  | { kind: "expired" }
  | { kind: "malformed"; reason: string }
  | { kind: "unsupportedSchema"; schema: number }
  | { kind: "oversizeStoryBlob"; bytes: number };

// ---------------------------------------------------------------------------
// StoryKey
// ---------------------------------------------------------------------------

/**
 * 32-byte opaque per-story symmetric key. Used directly as the AES-GCM key
 * (no HKDF needed — unlike ProfileKey, StoryKey is single-purpose).
 *
 * Mirrors iOS `StoryKey`.
 */
export class StoryKey {
  /** Raw 32-byte key material. */
  readonly raw: Uint8Array;

  private constructor(raw: Uint8Array) {
    this.raw = raw;
  }

  /** Create from existing bytes. Throws `invalidKeyLength` if not exactly 32. */
  static fromRaw(raw: Uint8Array): StoryKey {
    if (raw.length !== 32) {
      throw { kind: "invalidKeyLength", got: raw.length } satisfies StoryError;
    }
    return new StoryKey(raw.slice());
  }

  /** Generate a cryptographically-secure 32-byte random key. */
  static generate(): StoryKey {
    const raw = new Uint8Array(32);
    crypto.getRandomValues(raw);
    return new StoryKey(raw);
  }
}

// ---------------------------------------------------------------------------
// StoryMetadata
// ---------------------------------------------------------------------------

/**
 * Metadata for a story. This is the value that gets JSON-encoded (sorted keys)
 * and used as AES-GCM AAD, binding the ciphertext to these exact fields.
 *
 * Mirrors iOS `StoryMetadata`.
 */
export interface StoryMetadata {
  /** Always 1. */
  readonly schema: number;
  readonly storyId: string;
  readonly authorUserId: string;
  /** Unix seconds (integer). */
  readonly createdAt: number;
  /**
   * Video duration in whole seconds, or 5 for static-image stories (Signal-
   * style 5-second display). Carried in AAD so the server cannot relabel a
   * 30s video as a still image to dodge UX limits.
   */
  readonly durationSeconds: number;
  readonly mimeType: string;
}

/** Current story metadata schema version. */
export const STORY_METADATA_CURRENT_SCHEMA = 1;

/** Build a metadata object stamped with the current schema. */
export function makeStoryMetadata(
  storyId: string,
  authorUserId: string,
  durationSeconds: number,
  mimeType: string,
  createdAt: number = Math.floor(Date.now() / 1000),
): StoryMetadata {
  return {
    schema: STORY_METADATA_CURRENT_SCHEMA,
    storyId,
    authorUserId,
    createdAt,
    durationSeconds,
    mimeType,
  };
}

// ---------------------------------------------------------------------------
// StoryCipher
// ---------------------------------------------------------------------------

/** 50 MiB cap on encrypted-blob size (DoS floor; checked before AES-GCM). */
export const MAX_STORY_BLOB_BYTES = 50 * 1024 * 1024;

/** 24h in seconds — the client-enforced TTL boundary. */
export const STORY_TTL_SECONDS = 24 * 60 * 60;

/**
 * AES-GCM seal/open with AAD = canonical metadata JSON.
 * Mirrors iOS `StoryCipher`.
 */
export const StoryCipher = {
  /**
   * Encrypt `blobBytes` under `key` with AES-GCM; AAD = canonical
   * (sorted-keys) JSON bytes of `metadata`. Returns the combined encoding:
   * 12-byte nonce ‖ ciphertext ‖ 16-byte GCM tag.
   *
   * Throws `StoryError` on size overflow, AES-GCM failure, or metadata
   * encoding failure.
   */
  async seal(
    blobBytes: Uint8Array,
    metadata: StoryMetadata,
    key: StoryKey,
  ): Promise<Uint8Array> {
    if (blobBytes.length > MAX_STORY_BLOB_BYTES) {
      throw {
        kind: "oversizeStoryBlob",
        bytes: blobBytes.length,
      } satisfies StoryError;
    }
    const aad = canonicalAAD(metadata);
    const cryptoKey = await importKey(key);
    const nonce = new Uint8Array(12);
    crypto.getRandomValues(nonce);

    let cipherAndTag: ArrayBuffer;
    try {
      cipherAndTag = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv: nonce, additionalData: aad },
        cryptoKey,
        blobBytes,
      );
    } catch {
      throw { kind: "sealFailure" } satisfies StoryError;
    }

    // Combine: nonce (12) || ciphertext+tag
    const combined = new Uint8Array(12 + cipherAndTag.byteLength);
    combined.set(nonce, 0);
    combined.set(new Uint8Array(cipherAndTag), 12);
    return combined;
  },

  /**
   * Decrypt `encryptedBlob` (nonce ‖ ciphertext ‖ tag) with the given
   * `key`; AAD = canonical JSON of `metadata`. Checks TTL *before* AES-GCM.
   *
   * `at` is the caller-supplied "now" in Unix seconds — defaults to the real
   * wall clock. Pass an explicit value in tests to keep runs deterministic.
   *
   * TTL boundary is exclusive at exactly 24h (at == createdAt + 24h still
   * decrypts; at == createdAt + 24h + 1s does not — mirrors iOS).
   */
  async open(
    encryptedBlob: Uint8Array,
    metadata: StoryMetadata,
    key: StoryKey,
    at: number = Math.floor(Date.now() / 1000),
  ): Promise<Uint8Array> {
    if (encryptedBlob.length > MAX_STORY_BLOB_BYTES) {
      throw {
        kind: "oversizeStoryBlob",
        bytes: encryptedBlob.length,
      } satisfies StoryError;
    }

    // TTL check BEFORE AES-GCM — "at > createdAt + 24h" → expired.
    if (at > metadata.createdAt + STORY_TTL_SECONDS) {
      throw { kind: "expired" } satisfies StoryError;
    }

    if (encryptedBlob.length < 12) {
      throw { kind: "openFailure" } satisfies StoryError;
    }

    const aad = canonicalAAD(metadata);
    const cryptoKey = await importKey(key);
    const nonce = encryptedBlob.slice(0, 12);
    const cipherAndTag = encryptedBlob.slice(12);

    let plaintext: ArrayBuffer;
    try {
      plaintext = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: nonce, additionalData: aad },
        cryptoKey,
        cipherAndTag,
      );
    } catch {
      // AES-GCM auth failure: tampered ciphertext, wrong key, or metadata
      // mismatch all collapse to openFailure so the caller cannot distinguish
      // them and side-channel which ingredient was wrong.
      throw { kind: "openFailure" } satisfies StoryError;
    }

    return new Uint8Array(plaintext);
  },
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Returns the canonical (sorted-keys) JSON bytes of `metadata`.
 *
 * Sorted-key JSON is required so that sender and receiver independently
 * produce byte-identical AAD regardless of JS property ordering. AES-GCM is
 * sensitive to a single-byte AAD diff — without canonicalisation a sender
 * encoding `{a, b}` and a receiver encoding `{b, a}` would fail to decrypt.
 *
 * Mirrors iOS `StoryCipher.canonicalAAD(_:)`.
 */
function canonicalAAD(metadata: StoryMetadata): Uint8Array {
  if (metadata.schema !== STORY_METADATA_CURRENT_SCHEMA) {
    throw {
      kind: "unsupportedSchema",
      schema: metadata.schema,
    } satisfies StoryError;
  }
  // Sort all keys so the JSON is canonical regardless of insertion order.
  const sorted: Record<string, unknown> = {};
  for (const k of Object.keys(metadata).sort()) {
    sorted[k] = (metadata as unknown as Record<string, unknown>)[k];
  }
  return new TextEncoder().encode(JSON.stringify(sorted));
}

/** Import a StoryKey as a non-extractable AES-GCM CryptoKey. */
async function importKey(key: StoryKey): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    key.raw,
    { name: "AES-GCM" },
    /* extractable */ false,
    ["encrypt", "decrypt"],
  );
}
